import type { PowerUpDetail, PowerUpManifest } from '@molt/shared';
import { ArrowLeft, PlugZap } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { Button, EmptyState, Notice, Skeleton } from '@/components';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';
import { POWER_UP_CATEGORY_KEYS, POWER_UP_PRICING_KEYS } from './PowerUpDirectory';

type InterfaceTranslator = InterfaceLanguageContextValue['t'];

export interface PowerUpDetailViewProps {
  readonly powerUp: PowerUpDetail | null;
  readonly loading?: boolean | undefined;
  readonly error?: string | undefined;
  readonly enabledOnBoard?: boolean | undefined;
  readonly compact?: boolean | undefined;
  readonly action?: ReactNode | undefined;
  readonly onBack?: (() => void) | undefined;
  readonly onRetry: () => void;
}

function publisher(t: InterfaceTranslator, powerUp: PowerUpDetail): string {
  const text = powerUp.publisher?.trim() ?? '';
  return text.length === 0 ? t('powerUps.unknownPublisher') : text;
}

function description(t: InterfaceTranslator, powerUp: PowerUpDetail): string {
  const text = powerUp.description?.trim() ?? '';
  return text.length === 0 ? t('powerUps.noDescription') : text;
}

/** ⚠ The comma-join stays a join — see `PowerUpDirectory.permissionText`. */
function permissionText(t: InterfaceTranslator, powerUp: PowerUpDetail): string {
  const disclosure = powerUp.permissionsDisclosure?.trim() ?? '';
  if (disclosure.length > 0) return disclosure;
  if (powerUp.permissionGrants.length === 0) return t('powerUps.noPermissions');
  return powerUp.permissionGrants.map((grant) => grant.label).join(', ');
}

/**
 * ⚠ The nine group names, `Settings` and `Authorisation` are CHROME and are
 * routed; the labels they list are PROVIDER-declared and stay literal. The
 * whole line is one key, `{group}: {entries}`, so a locale keeps the colon and
 * the word order under its own control. The comma between provider labels is
 * the same untranslatable join as `permissionText`, reported as a residual.
 */
function manifestLines(t: InterfaceTranslator, manifest: PowerUpManifest): string[] {
  const lines: string[] = [];
  const groups: [string, PowerUpManifest[keyof PowerUpManifest]][] = [
    ['powerUps.detail.manifest.cardFrontBadges', manifest.cardFrontBadges],
    ['powerUps.detail.manifest.cardBackBadges', manifest.cardBackBadges],
    ['powerUps.detail.manifest.cardBackSections', manifest.cardBackSections],
    ['powerUps.detail.manifest.cardBackButtons', manifest.cardBackButtons],
    ['powerUps.detail.manifest.boardHeaderButtons', manifest.boardHeaderButtons],
    ['powerUps.detail.manifest.listMenuActions', manifest.listMenuActions],
    ['powerUps.detail.manifest.listMenuSorts', manifest.listMenuSorts],
    ['powerUps.detail.manifest.attachmentSections', manifest.attachmentSections],
    ['powerUps.detail.manifest.attachmentThumbnails', manifest.attachmentThumbnails],
  ];
  for (const [groupKey, value] of groups) {
    if (Array.isArray(value) && value.length > 0) {
      lines.push(
        t('powerUps.detail.manifestLine', {
          group: t(groupKey),
          entries: value.map((entry) => entry.label).join(', '),
        }),
      );
    }
  }
  if (manifest.settings !== null) {
    lines.push(
      t('powerUps.detail.manifestLine', {
        group: t('powerUps.detail.manifest.settings'),
        entries: manifest.settings.label,
      }),
    );
  }
  if (manifest.authorization !== null) {
    lines.push(
      t('powerUps.detail.manifestLine', {
        group: t('powerUps.detail.manifest.authorisation'),
        entries: manifest.authorization.label,
      }),
    );
  }
  return lines;
}

export function PowerUpDetailView({
  powerUp,
  loading = false,
  error,
  enabledOnBoard,
  compact = false,
  action,
  onBack,
  onRetry,
}: PowerUpDetailViewProps): ReactElement {
  const { t } = useInterfaceLanguage();

  if (loading) {
    return (
      <section
        aria-label={t('powerUps.detail.regionLabel')}
        aria-busy="true"
        className="flex flex-col gap-4"
      >
        {onBack === undefined ? null : (
          <Button
            type="button"
            size="sm"
            variant="quiet"
            iconStart={<ArrowLeft aria-hidden="true" className="size-4" />}
            onClick={onBack}
          >
            {t('powerUps.detail.back')}
          </Button>
        )}
        <Skeleton shape="block" className="h-72" />
      </section>
    );
  }

  if (error !== undefined) {
    return (
      <Notice
        tone="bad"
        title={t('powerUps.detail.loadFailedTitle')}
        body={error}
        action={
          <Button type="button" size="sm" variant="secondary" onClick={onRetry}>
            {t('powerUps.retry')}
          </Button>
        }
      />
    );
  }

  if (powerUp === null) {
    return (
      <EmptyState
        tone="neutral"
        headingLevel={compact ? 3 : 2}
        title={t('powerUps.detail.emptyTitle')}
        body={t('powerUps.detail.emptyBody')}
      />
    );
  }

  const name = powerUp.name.trim() === '' ? t('powerUps.untitledName') : powerUp.name;
  const additions = manifestLines(t, powerUp.manifest);

  return (
    <section
      aria-label={t('powerUps.detail.regionLabel')}
      className="flex flex-col gap-4 text-ink"
    >
      {onBack === undefined ? null : (
        <Button
          type="button"
          size="sm"
          variant="quiet"
          iconStart={<ArrowLeft aria-hidden="true" className="size-4" />}
          onClick={onBack}
        >
          {t('powerUps.detail.back')}
        </Button>
      )}

      <article
        aria-label={t('powerUps.detail.articleLabel', { powerUp: name })}
        className="flex flex-col gap-4 rounded-card border border-border bg-surface-raised p-4 shadow-card"
      >
        <div className="flex items-start gap-3">
          {powerUp.iconUrl === null ? (
            <span className="flex size-12 shrink-0 items-center justify-center rounded-card bg-surface text-accent">
              <PlugZap aria-hidden="true" className="size-6" />
            </span>
          ) : (
            <img
              src={powerUp.iconUrl}
              alt=""
              className="size-12 shrink-0 rounded-card bg-surface object-contain"
            />
          )}
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-xl font-semibold text-ink">
              {t('powerUps.detail.heading', { powerUp: name })}
            </h3>
            <p className="text-sm text-ink-muted">
              {t('powerUps.entryMeta', {
                category: t(POWER_UP_CATEGORY_KEYS[powerUp.category]),
                publisher: publisher(t, powerUp),
              })}
            </p>
          </div>
        </div>

        {enabledOnBoard === true ? (
          <p className="rounded-card border border-ok bg-surface px-3 py-2 text-sm font-medium text-ok">
            {t('powerUps.enabledBadge')}
          </p>
        ) : null}

        <p className="text-sm text-ink-muted">{description(t, powerUp)}</p>

        <dl className="grid gap-3 text-sm">
          <div>
            <dt className="font-medium text-ink">{t('powerUps.pricingTerm')}</dt>
            <dd className="text-ink-muted">
              {t(POWER_UP_PRICING_KEYS[powerUp.pricingModel])}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-ink">{t('powerUps.permissionsTerm')}</dt>
            <dd className="text-ink-muted">{permissionText(t, powerUp)}</dd>
          </div>
          <div>
            <dt className="font-medium text-ink">{t('powerUps.detail.additionsTerm')}</dt>
            <dd className="text-ink-muted">
              {additions.length === 0 ? (
                t('powerUps.detail.noAdditions')
              ) : (
                <ul className="list-disc space-y-1 pl-5">
                  {additions.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        </dl>

        {action}
      </article>
    </section>
  );
}
